import 'css/src/data/transcript/nodes/TextCorrectElementNode.module.scss';

import type {
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    SerializedLexicalNode,
    Spread,
} from 'lexical';
import { ElementNode } from 'lexical';

import { $createTranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { exportNodeToJSON } from 'js/src/data/transcript/transcript-utils';

function serializedNodeIsv0_1(
    node: SerializedLexicalNode,
): node is SerializedTextCorrectElementNode_v0_1 {
    return (
        node.version === 1 &&
        (node as SerializedTextCorrectElementNode_v0_1).originalWords !== undefined
    );
}
function serializedNodeIsv1(
    node: SerializedLexicalNode,
): node is SerializedTextCorrectElementNode_v1 {
    return (
        node.version === 1 &&
        (node as SerializedTextCorrectElementNode_v1).originalNodes !== undefined
    );
}

type SerializedTextCorrectElementNode_v0_1 = Spread<
    {
        originalWords: [
            {
                text: string;
                startTime: number;
                endTime: number;
            },
        ];
        type: 'text-correct-node';
        version: 1;
    },
    SerializedElementNode
>;

type SerializedTextCorrectElementNode_v1 = Spread<
    {
        originalNodes: SerializedLexicalNode[];
        type: 'text-correct-node';
        // Note: we did an improper migration of the TextCorrectElementNode - forgetting to bump the version number.
        // So both of the first two iterations are labeled version 1.
        version: 1;
    },
    SerializedElementNode
>;

export type SerializedTextCorrectElementNode =
    | SerializedTextCorrectElementNode_v0_1
    | SerializedTextCorrectElementNode_v1;

export class TextCorrectElementNode extends ElementNode {
    __originalNodes: SerializedLexicalNode[];

    constructor(originalNodes: SerializedLexicalNode[], key?: NodeKey) {
        super(key);
        this.__originalNodes = originalNodes;
    }

    static override getType(): string {
        return 'text-correct-node';
    }

    static override clone(node: TextCorrectElementNode): TextCorrectElementNode {
        return new TextCorrectElementNode(node.__originalNodes, node.__key);
    }

    override createDOM(): HTMLElement {
        const dom = document.createElement('span');
        dom.setAttribute('contenteditable', 'true');
        dom.setAttribute('data-testid', 'text-correct-input');
        dom.className = 'text-correct-input';
        return dom;
    }

    override updateDOM(): boolean {
        return false;
    }

    static override importJSON(
        serializedNode: SerializedTextCorrectElementNode,
    ): TextCorrectElementNode {
        let node = null;
        if (serializedNodeIsv0_1(serializedNode)) {
            const serializedNodes = serializedNode.originalWords.map((word) => {
                return $createTranscriptWordNode({
                    text: word.text,
                    startTime: word.startTime,
                    endTime: word.endTime,
                    isFiller: false, // there were no filler words in the original implementation
                }).exportJSON();
            });
            node = new TextCorrectElementNode(serializedNodes);
        } else if (serializedNodeIsv1(serializedNode)) {
            node = new TextCorrectElementNode(serializedNode.originalNodes);
        } else {
            throw new Error(
                `Unsupported TextCorrectElementNode version: ${JSON.stringify(serializedNode)}`,
            );
        }
        return node;
    }

    override exportJSON(): SerializedTextCorrectElementNode {
        return {
            ...super.exportJSON(),
            originalNodes: this.originalNodes,
            type: 'text-correct-node',
            version: 1,
        };
    }

    get originalNodes() {
        const self = this.getLatest();
        return self.__originalNodes;
    }

    override isInline(): boolean {
        // when lexical talks about "inline", it means that the node can be split into
        // multiple nodes, with TextNodes as siblings. This happens during paste operations,
        // for example.
        // However, only nodes that are inline can be empty. So if you delete all the content
        // inside of the node, lexical will add a `<br/>` tag to make sure that the node has some content.
        return false;
    }
}

export function $createTextCorrectElementNode(nodes: LexicalNode[]): TextCorrectElementNode {
    // Copy word nodes as text nodes and use to create a TextCorrectElementNode
    const elementNode = new TextCorrectElementNode(nodes.map((node) => exportNodeToJSON(node)));

    nodes.forEach((node) => {
        elementNode.append(node);
    });

    return elementNode;
}

export function $isTextCorrectElementNode(
    node: LexicalNode | undefined | null,
): node is TextCorrectElementNode {
    return node instanceof TextCorrectElementNode;
}
